import React, { useState } from 'react';

// ── These constants are copied 1:1 from ZPLGenerator so the preview can never drift from the real print again ──
const DPI = 300;
const DOTS_PER_CM = DPI / 2.54; // ≈ 118.11

const LABEL_HEIGHT_DOTS = 1772; // 15 cm
const LABEL_WIDTH_DOTS = 1181;  // 10 cm
const HEADER_HEIGHT_DOTS = 555; // 4.7 cm, pre-printed
const LEFT_MARGIN_DOTS = 50;
const CONTENT_WIDTH_DOTS = LABEL_WIDTH_DOTS - LEFT_MARGIN_DOTS * 2; // 1081

const QR_MAGNIFICATION = 9;
const QR_SIZE_DOTS = Math.round(3 * DOTS_PER_CM); // 354 — intended 3cm size
const QR_HORIZONTAL_OFFSET = 59; // measured correction, confirmed centered on real prints

// Fixed, measured Y positions — matches the current generateBadge()
const Y = {
  name: 620,
  title: 710,
  company: 785,
  country: 855,
  type: 920,
  qr: 680,
};

const QR_X_DOTS = Math.round((LABEL_WIDTH_DOTS - QR_SIZE_DOTS) / 2) + QR_HORIZONTAL_OFFSET;

const dotsToCm = (dots) => dots / DOTS_PER_CM;

const testData = {
  name: 'Martin Gammelholm',
  title: 'Solution Architect',
  company: 'Directions for Partners',
  country: 'Denmark',
  type: 'VIP',
};

// A stylised (non-scannable) QR mockup: three finder squares + a fixed module pattern,
// scaled to the real QR footprint so the proportions on screen match the real print.
function QrMock({ sizePx }) {
  const moduleCount = 21; // roughly a version-2 QR grid, just for visual density
  // A deterministic pseudo-pattern so it looks like data, not random noise every render
  const pattern = [
    1,0,1,1,0,1,0,0,1,1,0,1,0,1,1,0,0,1,0,1,1,
    1,0,0,1,1,0,1,1,0,0,1,0,1,0,0,1,1,0,1,0,0,
    1,0,1,0,1,1,0,1,1,0,1,1,0,1,1,0,1,0,1,1,0,
  ];

  return (
    <svg
      width={sizePx}
      height={sizePx}
      viewBox={`0 0 ${moduleCount} ${moduleCount}`}
      style={{ display: 'block' }}
    >
      <rect width={moduleCount} height={moduleCount} fill="#ffffff" />
      {/* module noise field */}
      {Array.from({ length: 9 }).map((_, row) =>
        Array.from({ length: moduleCount }).map((_, col) => {
          const idx = (row * moduleCount + col) % pattern.length;
          if (!pattern[idx]) return null;
          // skip the finder-pattern corners so they stay clean
          const inCorner =
            (row < 8 && col < 8) ||
            (row < 8 && col > moduleCount - 9) ||
            (row > 12 && col < 8);
          if (inCorner) return null;
          return (
            <rect
              key={`${row}-${col}`}
              x={col}
              y={row + 8}
              width={1}
              height={1}
              fill="#1a1a1a"
            />
          );
        })
      )}
      {/* three finder patterns */}
      {[
        [0, 0],
        [moduleCount - 7, 0],
        [0, moduleCount - 7],
      ].map(([fx, fy], i) => (
        <g key={i}>
          <rect x={fx} y={fy} width={7} height={7} fill="#1a1a1a" />
          <rect x={fx + 1} y={fy + 1} width={5} height={5} fill="#ffffff" />
          <rect x={fx + 2} y={fy + 2} width={3} height={3} fill="#1a1a1a" />
        </g>
      ))}
    </svg>
  );
}

function Label({ scale, rotated, label }) {
  const labelHeightPx = dotsToCm(LABEL_HEIGHT_DOTS) * scale;
  const labelWidthPx = dotsToCm(LABEL_WIDTH_DOTS) * scale;
  const headerHeightPx = dotsToCm(HEADER_HEIGHT_DOTS) * scale;
  const contentHeightPx = dotsToCm(LABEL_HEIGHT_DOTS - HEADER_HEIGHT_DOTS) * scale;
  const leftMarginPx = dotsToCm(LEFT_MARGIN_DOTS) * scale;
  const contentWidthPx = dotsToCm(CONTENT_WIDTH_DOTS) * scale;
  const qrSizePx = dotsToCm(QR_SIZE_DOTS) * scale;
  const qrLeftPx = dotsToCm(QR_X_DOTS) * scale;

  const fields = [
    { key: 'name', text: testData.name, y: Y.name, size: 15, weight: 700 },
    { key: 'title', text: testData.title, y: Y.title, size: 11, weight: 400 },
    { key: 'company', text: testData.company, y: Y.company, size: 10.5, weight: 400 },
    { key: 'country', text: testData.country, y: Y.country, size: 9.5, weight: 400 },
    { key: 'type', text: testData.type, y: Y.type, size: 9.5, weight: 700 },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>{label}</div>
      <div
        style={{
          width: labelWidthPx,
          height: labelHeightPx,
          transform: rotated ? 'rotate(180deg)' : 'none',
          border: '1.5px solid #2b2b2b',
          boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
          background: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Header — pre-printed area */}
        <div
          style={{
            height: headerHeightPx,
            background: 'linear-gradient(135deg, #4a4038 0%, #6b5b4f 55%, #8b7d77 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#e9e2da',
            fontSize: 10,
            letterSpacing: 0.4,
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 700 }}>directions for partners</div>
          <div style={{ opacity: 0.75, marginTop: 2 }}>pre-printed logo (4.7 cm)</div>
        </div>

        {/* Content — white printable area */}
        <div
          style={{
            height: contentHeightPx,
            position: 'relative',
            background: '#fffef9',
          }}
        >
          {/* center guide line */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 1,
              background: '#e2c9c9',
            }}
          />

          {fields.map((f) => (
            <div
              key={f.key}
              style={{
                position: 'absolute',
                top: dotsToCm(f.y - HEADER_HEIGHT_DOTS) * scale,
                left: leftMarginPx,
                width: contentWidthPx,
                textAlign: 'center',
                fontSize: f.size,
                fontWeight: f.weight,
                color: '#2a2a2a',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              {f.text}
            </div>
          ))}

          {/* QR code — positioned exactly like the real ZPL output */}
          <div
            style={{
              position: 'absolute',
              top: dotsToCm(Y.qr - HEADER_HEIGHT_DOTS) * scale,
              left: qrLeftPx,
              width: qrSizePx,
              height: qrSizePx,
            }}
          >
            <QrMock sizePx={qrSizePx} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LabelPreview() {
  const [scale, setScale] = useState(26); // px per cm

  const leftMarginCm = dotsToCm(QR_X_DOTS);
  const rightMarginCm = dotsToCm(LABEL_WIDTH_DOTS - QR_X_DOTS - QR_SIZE_DOTS);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', padding: 20, maxWidth: 980, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4, color: '#2a2a2a' }}>Badge Label Preview</h2>
      <p style={{ marginTop: 0, color: '#666', fontSize: 14 }}>
        Matcher konstanterne i <code>ZPLGenerator</code> 1:1 — ændrer du en værdi der, opdater den samme værdi her.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 22px' }}>
        <label style={{ fontSize: 13, color: '#444' }}>Zoom:</label>
        <input
          type="range"
          min={16}
          max={40}
          value={scale}
          onChange={(e) => setScale(Number(e.target.value))}
        />
      </div>

      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Label scale={scale} rotated={false} label="Side 1 — normal" />
        <Label scale={scale} rotated={true} label="Side 2 — roteret 180°" />
      </div>

      <div
        style={{
          marginTop: 30,
          background: '#f7f7f5',
          borderLeft: '4px solid #6b5b4f',
          borderRadius: 4,
          padding: '14px 18px',
        }}
      >
        <h3 style={{ marginTop: 0, fontSize: 14, color: '#6b5b4f' }}>Mål (300 DPI)</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {[
              ['Header højde', '4.7 cm = 555 dots'],
              ['Content højde', '9.2 cm = 1087 dots'],
              ['Label total højde', '15 cm = 1772 dots'],
              ['Label bredde', '10 cm = 1181 dots'],
              ['Venstre margin (tekst)', '50 dots'],
              ['Content bredde (tekst)', '1081 dots'],
              ['QR størrelse', `${QR_SIZE_DOTS} dots (≈3 cm), magnification ${QR_MAGNIFICATION}`],
              ['QR vandret offset', `${QR_HORIZONTAL_OFFSET} dots — empirisk korrektion, bekræftet centreret på rigtige print`],
              ['QR margin (venstre / højre)', `${leftMarginCm.toFixed(2)} cm / ${rightMarginCm.toFixed(2)} cm`],
              ['QR Y-position', `${Y.qr} dots — fast, målt værdi (ikke afledt af tekstens højde)`],
            ].map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid #e5e2dd' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600, width: '45%' }}>{label}</td>
                <td style={{ padding: '6px 8px', color: '#444' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 0 }}>
          Bemærk: venstre/højre QR-margin er ikke symmetrisk i tallene ovenfor — det er forventet, fordi
          offset'en er justeret efter et rigtigt print, ikke efter den teoretiske 3cm-bredde alene.
        </p>
      </div>
    </div>
  );
}